import React, { useState } from 'react';
import { MessageSquareText, Loader2 } from 'lucide-react';
import { FeedbackModal } from './FeedbackModal';
import { feedbackService } from '../../services/feedbackService';

interface FeedbackButtonProps {
  className?: string;
  iconClassName?: string;
  showLabel?: boolean;
  labelClassName?: string;
  tooltip?: string;
  variant?: 'floating' | 'header';
}

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({
  className = "",
  iconClassName = "w-6 h-6",
  showLabel = false,
  labelClassName = "",
  tooltip = "Enviar comentario",
  variant = 'floating'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [initialType, setInitialType] = useState<any>(undefined);
  const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined);

  React.useEffect(() => {
    const handleOpenEvent = async (e: any) => {
      const { initialType, initialMessage } = e.detail || {};
      setInitialType(initialType);
      setInitialMessage(initialMessage);
      
      setIsCapturing(true);
      const dataUrl = await feedbackService.captureScreenshot();
      setScreenshot(dataUrl);
      setIsCapturing(false);
      setIsOpen(true);
    };

    window.addEventListener('open-feedback-dialog', handleOpenEvent);
    return () => window.removeEventListener('open-feedback-dialog', handleOpenEvent);
  }, []);

  const handleOpen = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCapturing) return;
    
    setInitialType(undefined);
    setInitialMessage(undefined);
    
    setIsCapturing(true);
    // Brief delay to ensure any hover states, ripples or UI transitions settle
    // Increased to 300ms for better stability across devices
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      const dataUrl = await feedbackService.captureScreenshot();
      setScreenshot(dataUrl);
      if (!dataUrl) {
        console.warn('[FeedbackButton] Screenshot capture returned null, modal will open without preview.');
      }
    } catch (err) {
      console.error('[FeedbackButton] Error during capture:', err);
      setScreenshot(null);
    } finally {
      setIsCapturing(false);
      setIsOpen(true);
    }
  };

  return (
    <>
      <button
        id="feedback-button"
        onClick={handleOpen}
        disabled={isCapturing}
        className={className}
        title={tooltip}
      >
        {isCapturing ? (
          <Loader2 className={`${iconClassName} animate-spin text-primary-accent`} />
        ) : (
          <MessageSquareText className={`${iconClassName} transition-transform group-hover:scale-110`} />
        )}
        
        {showLabel && (
          <span className={labelClassName}>
            {isCapturing ? 'Capturando...' : 'Feedback'}
          </span>
        )}
        
        {/* Tooltip for desktop - only in floating mode */}
        {variant === 'floating' && (
          <div className="absolute right-full mr-3 px-3 py-1.5 bg-card-bg border border-grid-line rounded-lg text-[10px] font-black text-text-secondary uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-xl hidden md:block">
            {tooltip}
          </div>
        )}
      </button>

      <FeedbackModal 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)} 
        screenshot={screenshot} 
        initialType={initialType}
        initialMessage={initialMessage}
      />
    </>
  );
};
